import { DateTime } from 'luxon';

/**
 * Pure date-range primitives for analytics report cards. These mirror the
 * rules baked into Trusstor's analysis-center cards (e.g.
 * `getMinDateForIncidentReport`, the today-cap on overview cards) and let
 * each card compose its own constraint set without re-implementing the
 * timezone-correct arithmetic. JS Dates returned by these helpers represent
 * a calendar day in `tz` — they slot directly into `react-day-picker`'s
 * `before` / `after` matchers.
 *
 * Conventions:
 *   - `tz` is required at every call site. Analytics is always
 *     project-context-bound, so passing the working-site tz makes the
 *     dependency visible (and prevents JST-default drift on non-JST
 *     projects). Cards read it via `useSelector(selectSiteTz)`.
 *   - All bounds return *start of the project-tz day*, expressed as a
 *     local-tz Date whose calendar day matches the project's day (see
 *     `tzCalendarDayAsLocal`).
 */

/**
 * Construct a JS Date whose browser-local calendar day matches the supplied
 * project-tz DateTime's calendar day. `react-day-picker` is timezone-naive —
 * its `before` / `after` matchers compare calendar days in the user's local
 * tz. `toJSDate()` would return a UTC instant for project-tz midnight, which
 * renders as *yesterday* for any developer running west of the project tz.
 * Mirrors the convention encoded in `pickerDateToProjectNoonMs`: pickers
 * operate in local time, project-tz conversion happens at submission.
 */
export function tzCalendarDayAsLocal(dt: DateTime): Date {
  return new Date(dt.year, dt.month - 1, dt.day);
}

/**
 * Today (in `tz`), as a local-tz Date the calendar picker can compare against.
 * The natural upper bound for any "history" report — reports can't cover
 * future dates.
 */
export function todayCap(tz: string): Date {
  return tzCalendarDayAsLocal(DateTime.now().setZone(tz));
}

/**
 * `n` months ago (in `tz`), as a local-tz Date the calendar picker can compare
 * against. The Trusstor analogue (`getMinDateForIncidentReport`) uses 4 months
 * as the rolling investigation window — past that, the data is considered too
 * stale to incident-report against.
 */
export function rollingMonthsAgo(n: number, tz: string): Date {
  return tzCalendarDayAsLocal(DateTime.now().setZone(tz).minus({ months: n }));
}

/**
 * Parse a `YYYY-MM-DD` string into a Date whose browser-local calendar day
 * matches that calendar day. `new Date('2026-04-01')` parses as UTC midnight,
 * which shifts a day backward in any tz west of UTC. Same gotcha as
 * `tzCalendarDayAsLocal` for input strings instead of DateTimes.
 */
export function ymdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/**
 * Inverse of `ymdToLocalDate` — reads browser-local Y/M/D off a JS Date and
 * emits a `YYYY-MM-DD` string. Used on calendar-commit so the wire string
 * matches the calendar day the user clicked, regardless of system tz. Pairs
 * with `ymdToLocalDate` to keep the round-trip tz-naive.
 */
export function localDateToYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
